import { action, computed, observable, reaction } from "mobx";
import { FormStore } from "@modules/formx";
import {
  CustomerContractSnapshots_ContractInfoQuery,
  CustomerContractSnapshots_ContractInfo,
  CustomerContractSnapshot_ById,
  CustomerContractSnapshot_SubmitForm,
  CustomerContractSnapshots_ByContractId,
  PublishCustomerContractSnapshot,
  ConsultantContract_getContractsByConsultantId,
  CustomerContractSnapshot_Generate
} from "@graphql";
import { getObservableQuery } from "@graphql/getObservableQuery";
import { store } from "@store";
import locale from "@bonliva-crm/locale";
import { FormData } from "./utils";
import { defaultFields } from "./formSchemas";
import { FieldProps } from "@consultants/ConsultantContract/DynamicFieldsGroup";
import { openBlank } from "@clientUtils";

export { FormData };

export class SnapshotFormStore {
  form: FormStore<FormData>;
  snapshotId: string;
  handleClose: () => void;

  @observable loading = false;
  @observable isPublishingToOneflow = false;
  @observable isEdit = false;
  @observable isDuplicate = false;
  @observable fields: FieldProps[] = defaultFields;
  @observable preFilledData: FormData = null;
  @observable
  customerContract: CustomerContractSnapshots_ContractInfoQuery["customerContractById"];

  constructor(
    initialValues: FormData,
    contractId: string,
    handleClose,
    snapshotId?: string,
    action?: string
  ) {
    this.form = new FormStore<FormData>({
      onSubmit: this.onSubmit,
      initialValues
      // onSetValue: this._onSetValue
    });

    this.handleClose = handleClose;
    this.snapshotId = snapshotId;
    this.getCurrentCustomerContract(contractId);
    this.getSnapshot(snapshotId, action);

    this.isEdit = action === "edit";
    this.isDuplicate = action === "duplicate";

    reaction(
      () => this.preFilledData,
      (e) => {
        if (e) {
          this.form.reset(e);
        }
      }
    );
    (window as any).contractFormStore = this;
  }

  // _onSetValue = ({ name, value }) => {
  //   // change validation form when set ContractType to Ramavtal
  // };

  @computed get customers() {
    return this.customerContract?.orgCustomers ?? [];
  }

  @computed get allOfContactPerson() {
    return this.customerContract?.contactPerson ?? [];
  }

  @action setFormattedDataToForm = (data) => {
    this.preFilledData = data;
  };

  @action setConsultant = (consultant) => {
    this.form.setValue("consultantId", consultant._id);
  };

  @action getSnapshot = async (snapshotId, action) => {
    this.loading = true;
    try {
      if (action === "edit") {
        await CustomerContractSnapshot_ById.func(
          { snapshotId },
          { fetchPolicy: "network-only" }
        ).then((res) => {
          this.setFormattedDataToForm(res?.data?.customerContractSnapshotById);
        });
      } else if (action === "duplicate") {
        await CustomerContractSnapshot_ById.func(
          { snapshotId },
          { fetchPolicy: "network-only" }
        ).then((res) => {
          const { _id, ...rest } = res?.data?.customerContractSnapshotById ?? {};
          this.setFormattedDataToForm(rest);
        });
      }
      // else if (action === "forConfirmation") {
      // todo
      //   console.log("need to implement");
      // }
    } catch (e) {
      store().snackbars.error(`${e}`);
    } finally {
      this.loading = false;
    }
  };

  @action getCurrentCustomerContract = (id) => {
    CustomerContractSnapshots_ContractInfo.func(
      { customerContractId: id },
      { fetchPolicy: "network-only" }
    ).then((res) => {
      const { customerContractById } = res?.data ?? {};
      this.form.setValue("customerContractId", customerContractById._id);
      this.customerContract = customerContractById;
    });
  };

  download = (link: string) => {
    const a = document.createElement("a");
    a.href = link;
    a.click();
  };

  @action saveAndSendToOneflow = () => {
    this.isPublishingToOneflow = true;

    this.submit()
      .then(({ data }) => {
        return CustomerContractSnapshot_Generate.func(
          { snapshotId: data.submitCustomerContractSnapshotForm.value._id },
          {
            refetchQueries: [CustomerContractSnapshots_ByContractId.OperationName]
          }
        );
      })
      .then(({ data }) => {
        return PublishCustomerContractSnapshot.func({
          snapshotId: data.generateCustomerContractSnapshot.value._id
        });
      })
      .then((result) => {
        const url = result.data.publishCustomerContractSnapshot;
        openBlank(url);
        store().snackbars.success("Published");

        getObservableQuery(
          ConsultantContract_getContractsByConsultantId.OperationName
        )?.refetch();

        this.handleClose();
      })
      .catch((e) => {
        store().snackbars.error(`Error: ${e.toString()}`);
      })
      .finally(() => {
        this.isPublishingToOneflow = false;
      });
  };

  @action onSubmit = async () => {
    if (!this.form.valid) {
      return;
    }

    this.submit()
      .then((result) => {
        this.handleClose();
      })
      .catch((e) => {
        store().snackbars.error(`${e}`);
      })
      .finally(() => {
        this.loading = false;
      });
  };

  @action submit = () => {
    const { values } = this.form.state;

    this.loading = true;

    return CustomerContractSnapshot_SubmitForm.func(
      {
        snapshotId: this.isDuplicate ? null : this.snapshotId,
        data: values
      },
      { refetchQueries: [CustomerContractSnapshots_ByContractId.OperationName] }
    ).then((res) => {
      store().snackbars.success(
        this.isEdit ? locale.EditContractSuccess : locale.AddContractSuccess
      );

      return res;
    });
  };
}
